/**
 * Unified Cache Service for both BackEnd (Node) and UIX (Browser)
 */
export interface CacheEntry<T> {
    data: T;
    expiry: number;
}

export class CacheService {
    private cache: Map<string, CacheEntry<any>> = new Map();
    private defaultTtl: number;

    /**
     * @param defaultTtlMs Default Time To Live in milliseconds (default: 5 minutes)
     */
    constructor(defaultTtlMs: number = 5 * 60 * 1000) {
        this.defaultTtl = defaultTtlMs;
    }

    /**
     * Sets a value in the cache.
     * @param key Unique string identifier
     * @param data Data to cache
     * @param ttl Custom TTL for this entry (optional)
     */
    set<T>(key: string, data: T, ttl?: number): void {
        const expiry = Date.now() + (ttl ?? this.defaultTtl);
        this.cache.set(key, { data, expiry });
        
        // Cleanup expired entries periodically if needed, 
        // but for a lightweight service we just check on get.
    }

    /**
     * Retrieves a value from the cache if it exists and hasn't expired.
     */
    get<T>(key: string): T | null {
        const entry = this.cache.get(key);
        if (!entry) return null;

        if (Date.now() > entry.expiry) {
            this.cache.delete(key);
            return null;
        }

        return entry.data as T;
    }

    /**
     * Checks if a valid entry exists for the key.
     */
    has(key: string): boolean {
        const entry = this.cache.get(key);
        if (!entry) return false;

        if (Date.now() > entry.expiry) {
            this.cache.delete(key);
            return false;
        }

        return true;
    }

    /**
     * Removes an entry from the cache.
     */
    delete(key: string): void {
        this.cache.delete(key);
    }

    /**
     * Clears all entries.
     */
    clear(): void {
        this.cache.clear();
    }

    /**
     * Static helper to generate a stable string key from a complex object.
     * Useful for prompt-based caching.
     */
    static hashKey(obj: any): string {
        return JSON.stringify(obj, (key, value) => {
            if (value instanceof File) return `file:${value.name}:${value.size}`;
            return value;
        });
    }
}

// Global default instances
export const apiCache = new CacheService(10 * 60 * 1000); // 10 mins for API
export const aiCache = new CacheService(30 * 60 * 1000);  // 30 mins for AI responses
export const staticCache = new CacheService(60 * 60 * 1000); // 1 hour for static data
